import { getGatewayBaseUrl } from "../config";
import {
  calculateFinalPayableRounding,
  finalPayableClientSnapshot,
  type FinalPayableRoundingResult,
} from "../commerce/final-payable-rounding";
import {
  PosSaleGatewayClient,
  type PosSaleGatewayLine,
  type PosSalePromotionClaimEvidence,
  type PosSaleSyncResult,
} from "../gateway/pos-sale-client";
import type { CommerceCalculationRuntimeItem } from "../gateway/local-projection-client";
import type { CashShiftSnapshot, EmployeeRuntimeActor } from "../gateway/identity-client";
import { normalizePromotionClaimEvidence } from "../promotion/promotion-claim-session";
import { freshLocalSaleQuote } from "./cash-sale-state";
import type { LocalCartDraft, LocalSaleMember, LocalSaleQuote } from "./cart-state";
import {
  acknowledgeLocalCustomerBenefitUsage,
  writeLocalCustomerBenefitUsage,
} from "./customer-benefit-runtime";
import {
  clearLocalLoyaltyRedemption,
  readLocalLoyaltyRedemption,
  type LocalLoyaltyRedemption,
} from "./loyalty-redemption-state";
import { localCartSaleFingerprint } from "./cart-state";
import { posRuntimeDb, type JournalRecord, type OutboxRecord } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

export type LocalNonCashSaleSnapshot = {
  schema: "LOCAL_NONCASH_SALE_V1";
  localTransactionId: string;
  rootRequestKey: string;
  cartLocalId: string;
  organizationId: string;
  branchId: string;
  nodeId: string;
  commerceChannelId: string;
  performingEmployeeId: string;
  responsibleShiftEmployeeId: string;
  shiftId: string;
  catalogRevision: number;
  lines: LocalCartDraft["lines"];
  selectedMember: LocalSaleMember | null;
  claimedPromotionVersionIds: string[];
  quote: LocalSaleQuote | null;
  loyaltyRedemption: LocalLoyaltyRedemption | null;
  finalPayable?: FinalPayableRoundingResult;
  localTotal: number;
  currencyCode: string;
  paymentMethodCode: string;
  paymentMethodName: string;
  paymentRuntimeCapability: string;
  receivingDestinationId: string;
  receivingDestinationName: string;
  receivingRouteRole: "PRIMARY" | "FALLBACK";
  verificationSource: "STAFF_CONFIRMED";
  localCommittedAt: string;
  canonical: PosSaleSyncResult | null;
};

export type LocalNonCashPaymentSnapshot = {
  schema: "LOCAL_NONCASH_PAYMENT_ACCEPTANCE_V1";
  localTransactionId: string;
  rootRequestKey: string;
  paymentMethodCode: string;
  paymentMethodName: string;
  runtimeCapability: string;
  amount: number;
  currencyCode: string;
  receivingDestinationId: string;
  receivingDestinationName: string;
  receivingRouteRole: "PRIMARY" | "FALLBACK";
  verificationSource: "STAFF_CONFIRMED";
  performingEmployeeId: string;
  shiftId: string;
  localCommittedAt: string;
};

export type CommitLocalNonCashSaleInput = {
  identity: TrustedTerminalIdentity;
  actor: EmployeeRuntimeActor;
  shift: CashShiftSnapshot;
  cart: LocalCartDraft;
  catalogRevision: number;
  commerceCalculation: CommerceCalculationRuntimeItem;
  paymentMethodCode: string;
  paymentMethodName: string;
  paymentRuntimeCapability: string;
  receivingDestinationId: string;
  receivingDestinationName: string;
  receivingRouteRole: "PRIMARY" | "FALLBACK";
  onlineAtCommit: boolean;
  promotionClaimEvidence?: PosSalePromotionClaimEvidence[];
};

type NonCashSaleOutboxPayload = {
  localTransactionId: string;
  cartLocalId: string;
  lines: PosSaleGatewayLine[];
  catalogRevision: number;
  localTotal: number;
  localCommittedAt: string;
  executionMode: "ONLINE" | "OFFLINE";
  paymentMethodCode: string;
  receivingDestinationId: string;
  promotionClaimEvidence: PosSalePromotionClaimEvidence[];
  saleContext: Record<string, unknown>;
};

function randomId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function saleScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

function saleLocalId(localTransactionId: string) {
  return `sale:${localTransactionId}`;
}

function paymentLocalId(localTransactionId: string) {
  return `payment:${localTransactionId}:1`;
}

function customerClaimEvidence(value: PosSalePromotionClaimEvidence[]) {
  return value.filter((item): item is { claim_instance_id: string; customer_id: string } => "claim_instance_id" in item);
}

function bearerClaimEvidence(value: PosSalePromotionClaimEvidence[]) {
  return value.filter((item): item is { credential_code: string } => "credential_code" in item);
}

function snapshotFromJournal(record: JournalRecord) {
  const snapshot = record.snapshot as LocalNonCashSaleSnapshot;
  return snapshot?.schema === "LOCAL_NONCASH_SALE_V1" ? snapshot : null;
}

async function pendingIntentForCart(identity: TrustedTerminalIdentity, cartLocalId: string) {
  const scopeKey = saleScope(identity);
  const rows = await posRuntimeDb.journal
    .where("scopeKey")
    .equals(scopeKey)
    .filter((record) => record.kind === "SALE_ORDER" && record.state !== "CONFIRMED")
    .toArray();
  return rows.find((record) => snapshotFromJournal(record)?.cartLocalId === cartLocalId) ?? null;
}

function rewardFinalPayable(
  base: FinalPayableRoundingResult,
  redemption: LocalLoyaltyRedemption | null,
  cart: LocalCartDraft,
  currencyCode: string,
) {
  if (!redemption) return base;
  if (redemption.reservationState !== "RESERVED") throw new Error("LOYALTY_REWARD_RESERVATION_NOT_READY");
  if (redemption.validUntil && Date.parse(redemption.validUntil) <= Date.now()) throw new Error("LOYALTY_REWARD_RESERVATION_EXPIRED");
  if (!cart.selectedMember || cart.selectedMember.customerId !== redemption.customerId) {
    throw new Error("LOYALTY_REWARD_CART_CUSTOMER_MISMATCH");
  }
  const pricing = redemption.pricing;
  if (!pricing || pricing.cartFingerprint !== localCartSaleFingerprint(cart)) {
    throw new Error("LOYALTY_REWARD_QUOTE_REQUIRED");
  }
  if (pricing.currencyCode !== currencyCode) throw new Error("LOYALTY_REWARD_CURRENCY_MISMATCH");
  return {
    ...base,
    preRoundingTotal: Number((pricing.totalAmount - pricing.roundingAmount).toFixed(2)),
    roundingAmount: pricing.roundingAmount,
    totalAmount: pricing.totalAmount,
  } satisfies FinalPayableRoundingResult;
}

export async function commitLocalNonCashSale(input: CommitLocalNonCashSaleInput) {
  if (!input.cart.lines.length) throw new Error("NONCASH_SALE_CART_EMPTY");
  if (!input.actor.permission_codes.includes("commerce.sale.commit")) {
    throw new Error("COMMERCE_SALE_COMMIT_PERMISSION_REQUIRED");
  }
  if (input.shift.shift_status !== "OPEN") throw new Error("ACTIVE_POS_SHIFT_REQUIRED");
  if (!input.paymentMethodCode.trim()) throw new Error("PAYMENT_METHOD_REQUIRED");
  if (!input.paymentMethodName.trim()) throw new Error("PAYMENT_METHOD_NAME_REQUIRED");
  if (!input.receivingDestinationId.trim()) throw new Error("PAYMENT_RECEIVING_DESTINATION_REQUIRED");
  if (!["OFFLINE_CAPABLE", "OFFLINE_PENDING_ALLOWED"].includes(input.paymentRuntimeCapability)) {
    throw new Error("NONCASH_PAYMENT_LOCAL_COMMIT_NOT_ALLOWED");
  }
  if (input.cart.lines.some((line) => line.modifiers.some((modifier) => modifier.quantity !== 1))) {
    throw new Error("MODIFIER_QUANTITY_SYNC_NOT_SUPPORTED");
  }

  const existing = await pendingIntentForCart(input.identity, input.cart.localCartId);
  if (existing) {
    const snapshot = snapshotFromJournal(existing);
    if (!snapshot) throw new Error("NONCASH_SALE_PENDING_INTENT_INVALID");
    const selectedMethodCode = input.paymentMethodCode.trim().toUpperCase();
    const selectedDestinationId = input.receivingDestinationId.trim();
    if (
      snapshot.paymentMethodCode !== selectedMethodCode
      || snapshot.receivingDestinationId !== selectedDestinationId
    ) {
      throw new Error(
        `บิลนี้บันทึกในเครื่องแล้วด้วย ${snapshot.paymentMethodName} · ${snapshot.receivingDestinationName} ระบบจะใช้รายการเดิมเพื่อป้องกันการบันทึกซ้ำ`,
      );
    }
    return snapshot;
  }

  const currencyCode = input.cart.lines[0]?.currencyCode ?? "THB";
  if (input.cart.lines.some((line) => line.currencyCode !== currencyCode)) {
    throw new Error("NONCASH_SALE_CURRENCY_MISMATCH");
  }

  const quote = freshLocalSaleQuote(input.cart);
  if (!quote) throw new Error("POS_SALE_QUOTE_REQUIRED");
  const promotionClaimEvidence = normalizePromotionClaimEvidence(input.promotionClaimEvidence);
  const bearerClaims = bearerClaimEvidence(promotionClaimEvidence);
  const customerClaims = customerClaimEvidence(promotionClaimEvidence);
  if (bearerClaims.length && !input.onlineAtCommit) throw new Error("PROMOTION_CLAIM_REQUIRES_ONLINE");
  if (customerClaims.length) {
    if (!input.cart.selectedMember) throw new Error("PROMOTION_CLAIM_CUSTOMER_REQUIRED");
    if (customerClaims.some((item) => item.customer_id !== input.cart.selectedMember?.customerId)) {
      throw new Error("PROMOTION_CLAIM_CUSTOMER_MISMATCH");
    }
    if (!quote.appliedPromotions.some(
      (promotion) => promotion.activationCode === "CLAIM_CODE"
        && promotion.offlineCapability === "OFFLINE_RISK_LIMITED",
    )) {
      throw new Error("PROMOTION_CLAIM_QUOTE_VALIDATION_REQUIRED");
    }
  }
  const hasUnsupportedOfflinePromotion = quote.appliedPromotions.some((promotion) => {
    if (!promotion.offlineCapability || promotion.offlineCapability === "OFFLINE_SAFE") return false;
    return !(
      customerClaims.length
      && promotion.activationCode === "CLAIM_CODE"
      && promotion.offlineCapability === "OFFLINE_RISK_LIMITED"
    );
  });
  if (!input.onlineAtCommit && (quote.requiresOnlineResolution || hasUnsupportedOfflinePromotion)) {
    throw new Error("PROMOTION_CONTEXT_REQUIRES_ONLINE");
  }

  const redemption = await readLocalLoyaltyRedemption(input.identity, input.cart.requestKey);
  const baseFinalPayable = calculateFinalPayableRounding(quote.postPromotionTotal, input.commerceCalculation);
  const finalPayable = rewardFinalPayable(baseFinalPayable, redemption, input.cart, currencyCode);
  const localTotal = finalPayable.totalAmount;
  if (!Number.isFinite(localTotal) || localTotal <= 0) throw new Error("NONCASH_SALE_TOTAL_INVALID");

  const localTransactionId = randomId();
  const rootRequestKey = `pos-sale-v2:${randomId()}`;
  const localCommittedAt = new Date().toISOString();
  const scopeKey = saleScope(input.identity);
  const lines: PosSaleGatewayLine[] = input.cart.lines.map((line) => ({
    line_id: line.lineId,
    menu_item_id: line.menuItemId,
    menu_variant_id: line.menuVariantId,
    quantity: line.quantity,
    local_unit_price: line.unitPrice,
    price_version_id: line.priceVersionId,
    modifier_option_ids: line.modifiers.map((modifier) => modifier.modifierOptionId),
  }));

  const snapshot: LocalNonCashSaleSnapshot = {
    schema: "LOCAL_NONCASH_SALE_V1",
    localTransactionId,
    rootRequestKey,
    cartLocalId: input.cart.localCartId,
    organizationId: input.identity.organization_id,
    branchId: input.identity.branch_id,
    nodeId: input.identity.node_id,
    commerceChannelId: input.identity.commerce_channel_id,
    performingEmployeeId: input.actor.employee_id,
    responsibleShiftEmployeeId: input.shift.responsible_employee_id,
    shiftId: input.shift.shift_id,
    catalogRevision: input.catalogRevision,
    lines: input.cart.lines,
    selectedMember: input.cart.selectedMember,
    claimedPromotionVersionIds: [...input.cart.claimedPromotionVersionIds],
    quote,
    loyaltyRedemption: redemption,
    finalPayable,
    localTotal,
    currencyCode,
    paymentMethodCode: input.paymentMethodCode.trim().toUpperCase(),
    paymentMethodName: input.paymentMethodName.trim(),
    paymentRuntimeCapability: input.paymentRuntimeCapability,
    receivingDestinationId: input.receivingDestinationId,
    receivingDestinationName: input.receivingDestinationName,
    receivingRouteRole: input.receivingRouteRole,
    verificationSource: "STAFF_CONFIRMED",
    localCommittedAt,
    canonical: null,
  };

  const paymentSnapshot: LocalNonCashPaymentSnapshot = {
    schema: "LOCAL_NONCASH_PAYMENT_ACCEPTANCE_V1",
    localTransactionId,
    rootRequestKey,
    paymentMethodCode: snapshot.paymentMethodCode,
    paymentMethodName: snapshot.paymentMethodName,
    runtimeCapability: snapshot.paymentRuntimeCapability,
    amount: localTotal,
    currencyCode,
    receivingDestinationId: snapshot.receivingDestinationId,
    receivingDestinationName: snapshot.receivingDestinationName,
    receivingRouteRole: snapshot.receivingRouteRole,
    verificationSource: "STAFF_CONFIRMED",
    performingEmployeeId: input.actor.employee_id,
    shiftId: input.shift.shift_id,
    localCommittedAt,
  };

  const payload: NonCashSaleOutboxPayload = {
    localTransactionId,
    cartLocalId: input.cart.localCartId,
    lines,
    catalogRevision: input.catalogRevision,
    localTotal,
    localCommittedAt,
    executionMode: input.onlineAtCommit ? "ONLINE" : "OFFLINE",
    paymentMethodCode: snapshot.paymentMethodCode,
    receivingDestinationId: snapshot.receivingDestinationId,
    promotionClaimEvidence,
    saleContext: {
      customer_id: input.cart.selectedMember?.customerId ?? null,
      membership_id: input.cart.selectedMember?.membershipId ?? null,
      claimed_promotion_version_ids: input.cart.claimedPromotionVersionIds,
      loyalty_reward_redemption_id: redemption?.redemptionId ?? null,
      quote_fingerprint: quote.quoteFingerprint ?? null,
      quote_evaluated_at: quote.evaluatedAt ?? null,
      promotion_runtime_revision: quote.promotionRuntimeRevision ?? null,
      promotion_quote_source: quote.quoteSource ?? null,
      applied_promotion_version_ids: quote.appliedPromotions.map((promotion) => promotion.promotionVersionId),
      payment_runtime_capability: snapshot.paymentRuntimeCapability,
      payment_method_name: snapshot.paymentMethodName,
      receiving_route_role: snapshot.receivingRouteRole,
      final_payable: finalPayableClientSnapshot(finalPayable),
    },
  };

  const outbox: OutboxRecord = {
    requestKey: rootRequestKey,
    scopeKey,
    operation: "SYNC_COMMERCE_POS_SALE_V2_NONCASH",
    payload,
    state: "PENDING",
    attempts: 0,
    nextAttemptAt: null,
    lastError: null,
    createdAt: localCommittedAt,
    updatedAt: localCommittedAt,
  };

  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({
      localId: saleLocalId(localTransactionId),
      scopeKey,
      kind: "SALE_ORDER",
      state: "COMMITTED",
      requestKey: rootRequestKey,
      snapshot,
      createdAt: localCommittedAt,
      updatedAt: localCommittedAt,
    });
    await posRuntimeDb.journal.put({
      localId: paymentLocalId(localTransactionId),
      scopeKey,
      kind: "NONCASH_PAYMENT_ACCEPTANCE",
      state: "COMMITTED",
      requestKey: `${rootRequestKey}:PAYMENT:LOCAL`,
      snapshot: paymentSnapshot,
      createdAt: localCommittedAt,
      updatedAt: localCommittedAt,
    });
    if (redemption) {
      await writeLocalCustomerBenefitUsage(
        input.identity,
        { source_type: redemption.sourceType, source_instance_id: redemption.sourceInstanceId, customer_id: redemption.customerId },
        rootRequestKey,
        localTransactionId,
        localCommittedAt,
      );
    }
    for (const claim of customerClaims) {
      await writeLocalCustomerBenefitUsage(
        input.identity,
        { source_type: "PROMOTION_CLAIM", source_instance_id: claim.claim_instance_id, customer_id: claim.customer_id },
        rootRequestKey,
        localTransactionId,
        localCommittedAt,
      );
    }
    await posRuntimeDb.outbox.put(outbox);
    await posRuntimeDb.journal.delete(input.cart.localCartId);
  });

  return snapshot;
}

async function markOutboxFailure(record: OutboxRecord, message: string) {
  const now = new Date().toISOString();
  await posRuntimeDb.outbox.put({
    ...record,
    state: "FAILED",
    attempts: record.attempts + 1,
    nextAttemptAt: new Date(Date.now() + 5_000).toISOString(),
    lastError: message,
    updatedAt: now,
  });
}

export async function syncNonCashSaleOutbox(
  identity: TrustedTerminalIdentity,
  deviceSecret: string,
  sessionToken: string,
  requestKey: string,
) {
  const record = await posRuntimeDb.outbox.get(requestKey);
  if (!record || record.operation !== "SYNC_COMMERCE_POS_SALE_V2_NONCASH") {
    throw new Error("NONCASH_SALE_OUTBOX_NOT_FOUND");
  }

  const saleRecord = await posRuntimeDb.journal.where("requestKey").equals(requestKey).first();
  if (!saleRecord || saleRecord.kind !== "SALE_ORDER") throw new Error("LOCAL_SALE_JOURNAL_NOT_FOUND");
  const saleSnapshot = snapshotFromJournal(saleRecord);
  if (!saleSnapshot) throw new Error("LOCAL_NONCASH_SALE_SNAPSHOT_INVALID");

  if (record.state === "ACKED" && saleSnapshot.canonical) return saleSnapshot;

  const baseUrl = getGatewayBaseUrl();
  if (!baseUrl) {
    await markOutboxFailure(record, "POS_GATEWAY_NOT_CONFIGURED");
    throw new Error("POS_GATEWAY_NOT_CONFIGURED");
  }

  const payload = record.payload as NonCashSaleOutboxPayload;
  await posRuntimeDb.outbox.put({ ...record, state: "SENDING", updatedAt: new Date().toISOString() });

  const client = new PosSaleGatewayClient({ baseUrl });
  const result = await client.sync({
    deviceSecret,
    sessionToken,
    localTransactionId: payload.localTransactionId,
    lines: payload.lines,
    catalogRevision: payload.catalogRevision,
    localTotal: payload.localTotal,
    localCommittedAt: payload.localCommittedAt,
    executionMode: payload.executionMode,
    paymentMethodCode: payload.paymentMethodCode,
    receivingDestinationId: payload.receivingDestinationId,
    requestKey,
    promotionClaimEvidence: payload.promotionClaimEvidence,
    clientContext: {
      runtime: "WEBPOS",
      terminal_node_id: identity.node_id,
      local_transaction_id: payload.localTransactionId,
      local_commit_authority: false,
      local_operational_acceptance: true,
      sale_context: payload.saleContext,
    },
  });

  if (!result.ok || !result.data) {
    await markOutboxFailure(record, result.error ?? "COMMERCE_POS_SALE_V2_SYNC_FAILED");
    throw new Error(result.error ?? "COMMERCE_POS_SALE_V2_SYNC_FAILED");
  }

  const ackedAt = new Date().toISOString();
  const customerClaims = customerClaimEvidence(payload.promotionClaimEvidence);
  const sanitizedPayload: NonCashSaleOutboxPayload = { ...payload, promotionClaimEvidence: [] };
  const confirmedSnapshot: LocalNonCashSaleSnapshot = { ...saleSnapshot, canonical: result.data };
  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({
      ...saleRecord,
      state: "CONFIRMED",
      snapshot: confirmedSnapshot,
      updatedAt: ackedAt,
    });
    await posRuntimeDb.outbox.put({
      ...record,
      payload: sanitizedPayload,
      state: "ACKED",
      attempts: record.attempts + 1,
      nextAttemptAt: null,
      lastError: null,
      updatedAt: ackedAt,
    });
  });
  if (saleSnapshot.loyaltyRedemption) {
    await acknowledgeLocalCustomerBenefitUsage(
      identity,
      saleSnapshot.loyaltyRedemption.sourceType,
      saleSnapshot.loyaltyRedemption.sourceInstanceId,
    );
    await clearLocalLoyaltyRedemption(identity);
  }
  for (const claim of customerClaims) {
    await acknowledgeLocalCustomerBenefitUsage(identity, "PROMOTION_CLAIM", claim.claim_instance_id);
  }

  return confirmedSnapshot;
}

export async function syncPendingNonCashSales(identity: TrustedTerminalIdentity, deviceSecret: string, sessionToken: string) {
  const pending = await posRuntimeDb.outbox
    .where("operation")
    .equals("SYNC_COMMERCE_POS_SALE_V2_NONCASH")
    .filter((record) => record.state !== "ACKED")
    .sortBy("createdAt");

  const results: Array<{ requestKey: string; ok: boolean; error?: string }> = [];
  for (const record of pending) {
    if (record.scopeKey !== saleScope(identity)) continue;
    if (record.nextAttemptAt && Date.parse(record.nextAttemptAt) > Date.now()) continue;
    try {
      await syncNonCashSaleOutbox(identity, deviceSecret, sessionToken, record.requestKey);
      results.push({ requestKey: record.requestKey, ok: true });
    } catch (error) {
      results.push({
        requestKey: record.requestKey,
        ok: false,
        error: error instanceof Error ? error.message : "COMMERCE_POS_SALE_V2_SYNC_FAILED",
      });
    }
  }
  return results;
}
